import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { pullFollowUpReport } from '../utils/followUpReportPull';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { PROXY_REPORTS } from '../data/proxyReports';
import { BandChip } from '../components/proxy/ProxyChips';

// Outstanding-material report list. Deliberately separate from the Intake
// Queue: these are static snapshots of a job's true band, not a live action
// queue — a job appears here whether or not a follow-up has ever been
// requested for it.
export function ProxyReportsPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const { jobs } = useProxyData();
  const ctx = useProxyWorkflowContext();

  useEffect(() => {
    setTopbar('Proxy: Reports', "Static outstanding-material snapshots by cutoff band. These aren't follow-ups. Open a report to create one for a specific job.");
  }, [setTopbar]);

  // Same ICT-7 rules as the manual pull — Green is the section ICT-7 deletes.
  const counts = useMemo(() => {
    const pull = pullFollowUpReport(jobs, ctx.receipts, PROXY_SEED_TODAY);
    const result: Record<string, number> = {};
    for (const report of PROXY_REPORTS) {
      const green = pull.excluded.filter(e => e.step === 'green').length;
      result[report.id] = report.band === 'All' ? pull.rows.length + green
        : report.band === 'Green' ? green
          : pull.rows.filter(r => r.band === report.band).length;
    }
    return result;
  }, [jobs, ctx.receipts]);

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div className="flex items-center justify-between gap-4 mb-4 px-4 py-3 rounded-xl border border-trim bg-wash">
        <div className="text-xs text-copy">
          Alteryx emails these reports to ict@ each morning. If it's down, Ace pulls the report from Report Server and applies the ICT-7 rules for you.
        </div>
        <button
          data-tour="manual-pull-button"
          onClick={() => navigate('/proxy/reports/manual-pull')}
          className="text-xs font-semibold text-white bg-[#063F5A] rounded-lg px-3 py-2 whitespace-nowrap"
        >
          Manual pull (Alteryx down)
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {PROXY_REPORTS.map(report => (
          <button
            key={report.id}
            data-tour={`report-${report.id}`}
            onClick={() => navigate(`/proxy/reports/${report.id}`)}
            className="text-left px-4 py-3.5 rounded-xl border border-trim bg-white hover:bg-tint flex items-center justify-between gap-4"
          >
            <div>
              <div className="flex items-center gap-2">
                {report.band !== 'All' && <BandChip band={report.band} />}
                <span className="text-sm font-semibold text-ink">{report.title}</span>
              </div>
              <div className="text-xs text-muted mt-1 max-w-[600px]">{report.description}</div>
            </div>
            <span className="text-xs font-semibold whitespace-nowrap bg-wash border border-trim rounded-full px-3 py-1 text-copy">
              {counts[report.id] ?? 0} job{counts[report.id] === 1 ? '' : 's'}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
